/**
 * OCR Verification Module
 * Handles OCR processing for NID cards
 */

import Tesseract from 'tesseract.js';
import { ERROR_CODES, ERROR_MESSAGES } from '../config/constants';
import { dataURLToBlob } from '../utils/fileUtils';
import { preprocessImage } from '../utils/imageUtils';
import { parseNidBack, parseNidFront } from '../utils/ocrParsers';

class OcrVerification {
  constructor() {
    this.worker = null;
    this.isWorkerReady = false;
    this.progressCallback = null;
  }

  /**
   * Set progress callback for OCR operations
   */
  setProgressCallback(callback) {
    this.progressCallback = callback;
  }

  /**
   * Initialize Tesseract worker with Bengali and English languages
   * Optimized for NID card recognition
   */
  async initializeWorker() {
    if (this.worker && this.isWorkerReady) {
      return this.worker;
    }

    try {
      // Create worker with Bengali and English languages
      this.worker = await Tesseract.createWorker('ben+eng', 1, {
        logger: m => {
          if (m.status === 'recognizing text') {
            const progress = (m.progress * 100).toFixed(0);
            if (this.progressCallback) {
              this.progressCallback({
                status: m.status,
                progress: parseFloat(progress)
              });
            }
          }
        }
      });

      // Set optimized parameters for NID card recognition
      await this.worker.setParameters({
        // Page segmentation mode: Auto (best for mixed content)
        tessedit_pageseg_mode: Tesseract.PSM.AUTO,
        // OCR Engine mode: LSTM neural network
        tessedit_ocr_engine_mode: Tesseract.OEM.LSTM_ONLY,
        // Character whitelist for better accuracy
        tessedit_char_whitelist: '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyzঀঁংঃ঄অআইঈউঊঋঌএঐওঔকখগঘঙচছজঝঞটঠডঢণতথদধনপফবভমযরলশষসহ়ঽািীুূৃৄেৈোৌ্ৎড়ঢ়য়ৠৡৢৣ০১২৩৪৫৬৭৮৯-/. ',
        // Preserve line breaks
        preserve_interword_spaces: '1',
      });

      this.isWorkerReady = true;
      return this.worker;
    } catch (error) {
      console.error('Failed to initialize Tesseract worker:', error);
      throw new Error('Failed to initialize OCR engine');
    }
  }

  /**
   * Validate image data
   */
  validateImageData(imageData) {
    if (!imageData) {
      throw new Error('No image data provided');
    }

    if (typeof imageData === 'string' && !imageData.startsWith('data:image/')) {
      throw new Error('Invalid image data format');
    }
  }

  /**
   * Convert image data to Blob if needed
   */
  convertToBlob(imageData) {
    if (typeof imageData === 'string' && imageData.startsWith('data:')) {
      return dataURLToBlob(imageData);
    }
    return imageData;
  }

  /**
   * Perform OCR on NID front image
   * Extracts: Name (English), Name (Bangla), Father's Name, Mother's Name, NID Number, Date of Birth
   */
  async processNIDFront(imageData) {
    try {
      this.validateImageData(imageData);

      const worker = await this.initializeWorker();
      const preprocessedImage = preprocessImage(imageData);
      const imageToProcess = this.convertToBlob(preprocessedImage);

      const result = await worker.recognize(imageToProcess);
      const text = result.data.text;

      // Parse OCR text to extract required fields
      const parsedData = parseNidFront(text);

      return {
        success: true,
        data: parsedData,
        confidence: result.data.confidence,
        rawText: text
      };
    } catch (error) {
      console.error('NID Front OCR Error:', error);
      throw {
        errorCode: ERROR_CODES.E100,
        message: ERROR_MESSAGES.E100,
        details: error.message || error.toString(),
        originalError: error
      };
    }
  }

  /**
   * Perform OCR on NID back image
   * Extracts: Address
   */
  async processNIDBack(imageData) {
    try {
      this.validateImageData(imageData);

      const worker = await this.initializeWorker();
      const preprocessedImage = preprocessImage(imageData);
      const imageToProcess = this.convertToBlob(preprocessedImage);

      const result = await worker.recognize(imageToProcess);
      const text = result.data.text;

      // Parse OCR text to extract address
      const addressData = parseNidBack(text);

      return {
        success: true,
        data: addressData,
        confidence: result.data.confidence,
        rawText: text
      };
    } catch (error) {
      console.error('NID Back OCR Error:', error);
      throw {
        errorCode: ERROR_CODES.E100,
        message: ERROR_MESSAGES.E100,
        details: error.message || error.toString(),
        originalError: error
      };
    }
  }

  /**
   * Terminate the worker and free resources
   */
  async terminate() {
    if (this.worker) {
      await this.worker.terminate();
      this.worker = null;
      this.isWorkerReady = false;
    }
  }
}

// Export singleton instance
export default new OcrVerification();
